import {
  BREAKPOINTS_WIDTHS,
  formatMoney,
  template,
  useWindowSize,
} from '@belong/common';
import { IconInfo } from '@belong/icons';
import { Image, Money, Text, MoneyRange, Tooltip } from '@belong/ui';
import { MaintenancePriceRangeAggregateModel } from 'api/models';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { FinancingInfobox } from 'post-inspection/components/financing-infobox/financing-infobox';
import { STRINGS } from 'post-inspection/constants/strings';
import { useImprovementsTotalCost } from 'post-inspection/hooks/use-improvements-total-cost';
import type {
  HomeOwnerPayment,
  ReportPayment,
} from 'post-inspection/models/improvements-report';
import { STRINGS as EXECUTION_MODE_STRINGS } from 'pages/PostInspectionFlow/execution-mode/strings';
import { ownProCopyTokens } from 'pages/PostInspectionFlow/execution-mode/utils';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { MoveoutHomeownerImprovementsTotal } from '../moveout-homeowner-improvements-total/moveout-homeowner-improvements-total';

type Props = {
  homeownerPayment: HomeOwnerPayment;
  isLegacyInvoicing: boolean;
  isMonthly: boolean;
  inspectionType: {
    isPreMoveOut: boolean;
    isMoveOut: boolean;
    isOnboarding: boolean;
    isAdoptedLease: boolean;
  };
  maximumMonthlyPaymentPlanInstallmentCount: number;
  homeownerReportPayment: ReportPayment;
  priceRange?: MaintenancePriceRangeAggregateModel;
  displayNeverExceed?: boolean;
  ownProExclusion?: { count: number };
  hideFinancing?: boolean;
  dueNow?: number;
};

export const ImprovementsTotalCost = ({
  homeownerPayment,
  isLegacyInvoicing,
  isMonthly,
  inspectionType,
  maximumMonthlyPaymentPlanInstallmentCount,
  homeownerReportPayment,
  priceRange,
  displayNeverExceed,
  ownProExclusion,
  hideFinancing,
  dueNow = homeownerReportPayment?.groupDueNow,
}: Props) => {
  const { isPreMoveOut, isMoveOut, isOnboarding, isAdoptedLease } =
    inspectionType || {};
  const { totalCost } = useImprovementsTotalCost({
    homeownerPayment,
    isLegacyInvoicing,
    isMonthly,
    maximumMonthlyPaymentPlanInstallmentCount,
    homeownerReportPayment,
  });

  const homeownerRange = priceRange?.priceRangeBreakdown?.find(
    (range) => range.paidBy === 'Homeowner',
  );

  const [totalCostDollars, totalCostCents] = String(
    formatMoney(totalCost, 'DOLLARS_WITH_CENTS'),
  ).split('.');

  const { width } = useWindowSize();
  const { isEnabled: isSplitItEnabled } = useFeatureFlag('enable-split-it');
  const showFinancing = !hideFinancing && isSplitItEnabled;

  // todo: we may want to move this helpers to @belong/common
  const isMobile = width <= BREAKPOINTS_WIDTHS.SM;

  if (
    (isMoveOut && !displayNeverExceed) ||
    (isOnboarding && isAdoptedLease && displayNeverExceed)
  ) {
    return (
      <MoveoutHomeownerImprovementsTotal
        {...{ totalCost, homeownerReportPayment }}
      />
    );
  }

  if (isPreMoveOut) {
    return <FinancingInfobox />;
  }

  if (isLegacyInvoicing) {
    return (
      <div className="flex justify-end">
        <Money
          fontWeight="semibold"
          format="DOLLARS_WITH_CENTS"
          value={totalCost}
          variant="h3"
        />
      </div>
    );
  }

  const isHomeownerPaymentZero =
    homeownerReportPayment.monthlyPayment === 0 &&
    homeownerReportPayment.total === 0 &&
    homeownerReportPayment.groupDueNow === 0;

  if (isHomeownerPaymentZero) {
    return null;
  }

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-start ${
        showFinancing ? 'sm:justify-between' : 'sm:justify-end'
      }`}
    >
      {showFinancing && (
        <div className="mb-lg flex items-start sm:mb-0">
          <div className="mr-xs">
            <Image
              alt="plan-it"
              className="h-auto w-xl"
              src="/improvement-reports/plan.svg"
            />
          </div>
          <div className="flex flex-col gap-2xs">
            <div>
              <Text variant={isMobile ? 'p1' : 'body'}>
                {POST_INSPECTION_FLOW_STRINGS['approvals.payment.plan']}
              </Text>
            </div>
            <div className="flex">
              <Text
                as="span"
                fontWeight="semibold"
                variant={isMobile ? 'p1' : 'body'}
              >
                {POST_INSPECTION_FLOW_STRINGS['approvals.est']}
              </Text>
              &nbsp;
              <Money
                as="span"
                fontWeight="semibold"
                format="DOLLARS_NO_CENTS"
                value={homeownerReportPayment.monthlyPayment}
                variant={isMobile ? 'p1' : 'body'}
              />
              <Text
                as="span"
                fontWeight="semibold"
                variant={isMobile ? 'p1' : 'body'}
              >
                /Mo.
              </Text>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-col gap-xs text-right">
          <div className="flex items-end justify-end">
            {!displayNeverExceed && (
              <>
                <Text fontWeight="semibold">
                  {POST_INSPECTION_FLOW_STRINGS['approvals.est.total']}
                </Text>
                &nbsp;
              </>
            )}
            {priceRange ? (
              <div className="flex items-end gap-xs">
                <MoneyRange
                  // Asserted: reached only when `priceRange` is set, which TS does
                  // not carry across to the `find` result above.
                  lower={homeownerRange!.lowerBoundTotal}
                  upper={homeownerRange!.upperBoundTotal}
                  className="h3 font-semibold"
                  format="DOLLARS_WITH_CENTS"
                />
                <Tooltip
                  contentProps={{ className: 'z-50' }}
                  content={
                    <div className="max-w-md rounded bg-black p-2sm ">
                      <p className="p1 font-regular text-white">
                        We&apos;ve calculated a repair range factoring in
                        potential scope changes that can affect the final cost
                        once a contractor takes a closer look. Belong will
                        negotiate the lowest final price with no mark-up.
                        Greenlighting this range empowers our team to start work
                        promptly.
                      </p>
                    </div>
                  }
                  arrowProps={{
                    className: 'fill-bg-black',
                  }}
                >
                  <button
                    className="cursor-pointer fill-navy"
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconInfo width={20} />
                  </button>
                </Tooltip>
              </div>
            ) : displayNeverExceed ? (
              <div className="flex items-end justify-end gap-2xs">
                <Text as="span" variant="p1" className="whitespace-nowrap">
                  Never Exceed
                </Text>

                <Text
                  as="span"
                  fontWeight="semibold"
                  className="text-h3-xl leading-p1"
                >
                  {totalCostDollars}
                  <span className="text-body">.{totalCostCents}</span>
                </Text>

                <Tooltip
                  contentProps={{ className: 'z-50' }}
                  content={
                    <div className="max-w-md rounded border-dark-navy bg-dark-navy p-2sm">
                      <p className="p1 text-left font-regular text-white">
                        We guarantee you&apos;ll never pay more than this
                        approved amount. If costs exceed it, Belong will cover
                        the difference.
                        {ownProExclusion && (
                          <>
                            {' '}
                            {template(
                              EXECUTION_MODE_STRINGS.neverExceed.tooltip,
                              ownProCopyTokens(ownProExclusion.count),
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  }
                  arrowProps={{
                    className: 'fill-dark-navy border-dark-navy',
                  }}
                >
                  <button
                    className="cursor-pointer fill-navy"
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconInfo width={18} />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <Money
                fontWeight="semibold"
                format="DOLLARS_WITH_CENTS"
                value={totalCost}
                variant="h3"
              />
            )}
          </div>

          {Boolean(dueNow) && (
            <div>
              <Text as="span" variant={isMobile ? 'p1' : 'body'}>
                {STRINGS['due-now']}
              </Text>
              &nbsp;
              <Money
                as="span"
                format="DOLLARS_WITH_CENTS"
                value={dueNow}
                variant={isMobile ? 'p1' : 'body'}
              />
            </div>
          )}

          {displayNeverExceed && ownProExclusion && (
            <Text variant="p1" className="text-dark-gray">
              <Text
                as="span"
                variant="p1"
                fontWeight="semibold"
                className="text-dark-gray"
              >
                {EXECUTION_MODE_STRINGS.neverExceed.excludes}
              </Text>{' '}
              {template(
                EXECUTION_MODE_STRINGS.neverExceed.excludesRest,
                ownProCopyTokens(ownProExclusion.count),
              )}{' '}
              {EXECUTION_MODE_STRINGS.neverExceed.excludesQuoteNote}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
